/* Rota pública mobile-friendly — motorista acessa via link/token sem login.
   URL: /rota/:token */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { Truck, MapPin, Phone, CheckCircle2, ExternalLink, Loader2, Route } from "lucide-react";
import { toast } from "sonner";
import {
  STATUS_PARADA, PRIORIDADES, badge, linkGoogleMaps, linkWaze,
} from "./_lib";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function RotaMotoristaPublica() {
  const { token } = useParams();
  const [rota, setRota] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [checkin, setCheckin] = useState(null);
  const [obs, setObs] = useState("");
  const [busy, setBusy] = useState(false);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/publico/rota/${token}`);
      setRota(data); setErro(null);
    } catch (e) {
      setErro(e?.response?.data?.detail || "Não foi possível abrir a rota");
    } finally { setLoading(false); }
  };
  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [token]);

  const confirmarCheckin = async () => {
    if (!checkin) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/publico/rota/${token}/parada/${checkin.id}/checkin`, { observacao: obs });
      toast.success("Check-in registrado!");
      setCheckin(null); setObs("");
      carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no check-in");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="animate-spin inline" /></div>;
  if (erro) return (
    <div className="p-8 text-center max-w-md mx-auto">
      <Card className="p-6">
        <h2 className="font-bold text-red-700">Rota indisponível</h2>
        <p className="text-sm text-muted-foreground mt-2">{erro}</p>
      </Card>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto p-3 pb-20" data-testid="rota-publica-page">
      {/* Header */}
      <div className="bg-gradient-to-br rounded-lg p-4 text-white mb-4" style={{ background: "linear-gradient(135deg, #1B3A6B, #2E5A9D)" }}>
        <div className="flex items-center gap-2">
          <Truck size={22} />
          <div>
            <div className="font-bold text-lg">Rota {rota.numero}</div>
            <div className="text-xs opacity-90">{rota.data} · {rota.motorista_nome || "Motorista"} · {rota.veiculo_placa || ""}</div>
          </div>
        </div>
      </div>

      {/* Paradas */}
      <div className="space-y-3">
        {(rota.paradas || []).map((p) => {
          const bp = badge(p.status, STATUS_PARADA);
          const bpr = badge(p.prioridade, PRIORIDADES);
          const concluido = p.status === "concluido";
          return (
            <Card key={p.id} className={`p-3 ${concluido ? "opacity-60" : ""}`} data-testid={`parada-pub-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold" style={{ color: "#1B3A6B" }}>{p.ordem}</span>
                    <div>
                      <div className="font-bold text-sm">{p.cliente_nome}</div>
                      <div className="text-[10px] text-muted-foreground flex items-center gap-1"><MapPin size={10} /> {p.endereco}</div>
                      <div className="text-[10px] text-muted-foreground">{p.cidade}{p.uf ? `/${p.uf}` : ""}</div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-1 items-end">
                  <Badge className={bp.cls + " border text-[10px]"}>{bp.label}</Badge>
                  <Badge className={bpr.cls + " border text-[10px]"}>{bpr.label}</Badge>
                </div>
              </div>

              {(p.equipamentos || []).length > 0 && (
                <div className="mt-2 text-[10px] text-muted-foreground">
                  <strong>Equipamentos:</strong> {(p.equipamentos || []).join(", ")}
                </div>
              )}
              {p.observacoes && (
                <div className="mt-1 text-[10px] italic">{p.observacoes}</div>
              )}

              <div className="flex flex-wrap gap-2 mt-3">
                <a href={linkGoogleMaps(p.endereco, p.cidade, p.uf)} target="_blank" rel="noopener noreferrer" className="flex-1">
                  <Button size="sm" variant="outline" className="w-full" data-testid={`pub-maps-${p.id}`}>
                    <ExternalLink size={12} className="mr-1" /> Google Maps
                  </Button>
                </a>
                <a href={linkWaze(p.endereco, p.cidade, p.uf)} target="_blank" rel="noopener noreferrer" className="flex-1">
                  <Button size="sm" variant="outline" className="w-full bg-blue-50 text-blue-800" data-testid={`pub-waze-${p.id}`}>
                    <ExternalLink size={12} className="mr-1" /> Waze
                  </Button>
                </a>
                {p.telefone && (
                  <a href={`tel:${p.telefone}`}>
                    <Button size="sm" variant="outline" data-testid={`pub-tel-${p.id}`}>
                      <Phone size={12} />
                    </Button>
                  </a>
                )}
                {!concluido && (
                  <Button
                    size="sm"
                    onClick={() => setCheckin(p)}
                    className="text-white"
                    style={{ background: "#16a34a" }}
                    data-testid={`pub-checkin-${p.id}`}
                  >
                    <CheckCircle2 size={12} className="mr-1" /> Atendido
                  </Button>
                )}
              </div>
            </Card>
          );
        })}
        {(rota.paradas || []).length === 0 && (
          <Card className="p-4 text-center text-sm text-muted-foreground">
            Sem paradas cadastradas nesta rota.
          </Card>
        )}
      </div>

      <Dialog open={!!checkin} onOpenChange={(o) => !o && setCheckin(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Marcar atendido</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm"><strong>Cliente:</strong> {checkin?.cliente_nome}</p>
            <p className="text-xs text-muted-foreground"><Route size={10} className="inline" /> Check-in é opcional. A observação ajuda no histórico.</p>
            <Textarea
              rows={3}
              value={obs}
              onChange={(e) => setObs(e.target.value)}
              placeholder="Observação (opcional). Ex: entregue ao Carlos, recebimento OK."
              data-testid="pub-obs-checkin"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCheckin(null)}>Cancelar</Button>
            <Button onClick={confirmarCheckin} disabled={busy} className="text-white" style={{ background: "#16a34a" }} data-testid="pub-confirmar-checkin">
              {busy ? <Loader2 className="animate-spin mr-1" size={14} /> : <CheckCircle2 size={14} className="mr-1" />}
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
